'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import Lottie from 'lottie-react';
import Image from 'next/image';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ServiceList from '@/components/Services/ServiceList';
import { ServicesDto } from '@/components/Services/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import VideoPreview from '@/components/VideoPreview';
import { useAboutUsVideo } from '@/contexts/AboutUsVideoContext';
import { useCustomCursor } from '@/hooks/useCustomCursor';
import useDevice from '@/hooks/useDevice';
import { dispatchBrushReady } from '@/hooks/useLoader';
import useLottieHandlers from '@/hooks/useLottieHandlers';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { loadBrushLottie } from '@/lib/brush-lottie';
import { gsap, ScrollTrigger, SplitText } from '@/lib/gsap';
import css from './style.module.scss';
interface Props {
  data: ServicesDto;
  htmlId?: string;
  scrollTriggerBackStart?: string;
  disableLottie?: boolean;
  headingTag?: 'h1' | 'h2';
}
const Services: FC<Props> = t0 => {
  const $ = _c(73);
  const {
    data,
    htmlId,
    scrollTriggerBackStart: t1,
    disableLottie: t2,
    headingTag: t3
  } = t0;
  const scrollTriggerBackStart = t1 === undefined ? "top+=1 top" : t1;
  const disableLottie = t2 === undefined ? false : t2;
  const headingTag = t3 === undefined ? "h2" : t3;
  const {
    title,
    serviceList
  } = data;
  const device = useDevice();
  const aboutUsVideo = useAboutUsVideo();
  const {
    lottieRef,
    playForwardAnimHandler,
    playBackAnimHandler,
    playAnimHandler,
    setSpeedHandler
  } = useLottieHandlers();
  const {
    setCursorVariant
  } = useCustomCursor();
  const tl = useRef(null);
  const wrapperRef = useRef(null);
  const chubiEndRef = useRef(null);
  const [brushData, setBrushData] = useState(null);
  const shouldLoadBrush = !disableLottie && device === "desktop";
  let t4;
  let t5;
  if ($[0] !== shouldLoadBrush) {
    t4 = () => {
      if (!shouldLoadBrush) {
        return;
      }
      let cancelled = false;
      const loadBrush = async () => {
        const json = await loadBrushLottie();
        if (cancelled) {
          return;
        }
        setBrushData(json);
        if (!json) {
          dispatchBrushReady();
        }
      };
      loadBrush();
      return () => {
        cancelled = true;
      };
    };
    t5 = [shouldLoadBrush];
    $[0] = shouldLoadBrush;
    $[1] = t4;
    $[2] = t5;
  } else {
    t4 = $[1];
    t5 = $[2];
  }
  useEffect(t4, t5);
  let t6;
  if ($[3] !== brushData || $[4] !== lottieRef) {
    t6 = () => {
      if (!brushData) {
        return;
      }
      lottieRef.current?.setSpeed(0.75);
      dispatchBrushReady();
    };
    $[3] = brushData;
    $[4] = lottieRef;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== brushData) {
    t7 = [brushData];
    $[6] = brushData;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [wrapperRef];
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  useViewportVideosPlayback(t8);
  let t9;
  if ($[9] !== disableLottie || $[10] !== lottieRef || $[11] !== playAnimHandler || $[12] !== playBackAnimHandler || $[13] !== playForwardAnimHandler || $[14] !== scrollTriggerBackStart || $[15] !== setSpeedHandler) {
    t9 = () => {
      const wrapper = wrapperRef.current;
      const chubiEnd = chubiEndRef.current;
      const graffiti = wrapper?.querySelector(`.${css.graffitiBg}`);
      const videoContainer = wrapper?.querySelector(`.${css.videoWrapper}`);
      const titleText = wrapper?.querySelectorAll(`.${css.title} p`);
      const service = wrapper?.querySelector(`.${css.service}`);
      const circle = wrapper?.querySelector(`.${css.curvedText}`);
      const curvedContainer = wrapper?.querySelector(`.${css.curvedWrapper}`);
      const splitServiceTitles = wrapper?.querySelectorAll(`.${css.serviceTitle} p`);
      const splitServiceDescs = wrapper?.querySelectorAll(`.${css.serviceDesc} p`);
      const emptyBlock = wrapper?.querySelector(`.${css.empty}`);
      if (!disableLottie) {
        lottieRef.current?.setSpeed(0.75);
      }
      if (!wrapper || !videoContainer || !circle || !curvedContainer || !chubiEnd || !titleText || !service || !splitServiceTitles || !splitServiceDescs || !graffiti) {
        return;
      }
      const split = SplitText.create(titleText, {
        type: "lines",
        linesClass: "line",
        mask: "lines"
      });
      const ctx = gsap.context(() => {
        const matchMedia = gsap.matchMedia();
        matchMedia.add({
          isDesktop: "(min-width: 992px)",
          isMobile: "(max-width: 991px)"
        }, context => {
          const {
            isMobile
          } = context.conditions as gsap.Conditions;
          if (emptyBlock) {
            gsap.fromTo(emptyBlock, {
              height: "20vh"
            }, {
              height: "0vh",
              ease: "none",
              scrollTrigger: {
                trigger: emptyBlock,
                start: "top bottom",
                end: "top 70%",
                scrub: true,
                toggleActions: "play none none reverse"
              }
            });
          }
          if (isMobile) {
            gsap.set(split.lines, {
              yPercent: 0,
              opacity: 1
            });
          } else {
            gsap.set(split.lines, {
              yPercent: 100,
              opacity: 0
            });
            gsap.to(split.lines, {
              yPercent: 0,
              opacity: 1,
              duration: 0.9,
              ease: "power2.out",
              stagger: {
                each: 0.08,
                from: "start"
              },
              scrollTrigger: {
                trigger: titleText,
                start: "top 90%",
                end: "bottom 60%",
                toggleActions: "play none none reverse"
              }
            });
          }
          gsap.to(curvedContainer, {
            y: isMobile ? 0 : "-50vh",
            ease: "none",
            scrollTrigger: {
              trigger: curvedContainer,
              start: "center",
              endTrigger: wrapper,
              end: "bottom center",
              scrub: true
            }
          });
          tl.current = gsap.timeline({
            paused: true
          });
          let sectionPhase = "idle";
          const setSectionForward = () => {
            if (isMobile) {
              return;
            }
            if (sectionPhase === "forward") {
              return;
            }
            sectionPhase = "forward";
            if (!disableLottie) {
              playForwardAnimHandler();
              playAnimHandler();
              setSpeedHandler(0.5);
              tl.current?.play();
              gsap.set(wrapper, {
                pointerEvents: "auto"
              });
            }
          };
          const setSectionReverse = () => {
            if (isMobile) {
              return;
            }
            if (sectionPhase === "reverse") {
              return;
            }
            sectionPhase = "reverse";
            if (!disableLottie) {
              playBackAnimHandler();
              playAnimHandler();
              tl.current?.reverse();
              gsap.set(wrapper, {
                pointerEvents: "none"
              });
            }
          };
          ScrollTrigger.create({
            trigger: wrapper,
            start: "top+=1 top",
            end: "bottom",
            onEnter: setSectionForward,
            onEnterBack: setSectionForward,
            onLeaveBack: setSectionReverse,
            onUpdate: t10 => {
              const {
                progress
              } = t10;
              const scrollProgress = progress * 360;
              gsap.to(circle, {
                rotate: scrollProgress,
                ease: "none",
                duration: 0
              });
            }
          });
          ScrollTrigger.create({
            trigger: wrapper,
            start: scrollTriggerBackStart,
            end: "bottom",
            onEnterBack: setSectionForward,
            onLeaveBack: setSectionReverse
          });
          if (isMobile) {
            return;
          }
          const spacerEl = disableLottie ? wrapper.querySelector(`.${css.lottieContainerSpacer}`) as HTMLElement | null : null;
          const introScrollOffset = (() => {
            if (!spacerEl) {
              return "20vh";
            }
            const {
              offsetHeight
            } = spacerEl;
            if (offsetHeight > 0) {
              return `${offsetHeight}px`;
            }
            const cssHeight = getComputedStyle(spacerEl).getPropertyValue("--services-intro-height").trim();
            return cssHeight || "20vh";
          })();
          const introScrollEnd = disableLottie ? `top+=${introScrollOffset} top` : "top top";
          const pinStart = disableLottie ? introScrollEnd : "top top";
          if (disableLottie) {
            gsap.set(videoContainer, {
              yPercent: 100,
              top: 0,
              clearProps: "transform,left,right,x,xPercent"
            });
          }
          if (!disableLottie) {
            const spawnAnimation = gsap.to(videoContainer, {
              top: "-100vh",
              yPercent: -30,
              duration: 1,
              ease: "expo.out"
            });
            tl.current.add([spawnAnimation]);
          }
          if (disableLottie) {
            gsap.to(videoContainer, {
              top: 0,
              yPercent: 0,
              ease: "none",
              scrollTrigger: {
                trigger: wrapper,
                start: "top top",
                end: introScrollEnd,
                scrub: true
              }
            });
          }
          gsap.to(graffiti, {
            yPercent: -60,
            ease: disableLottie ? "none" : "expo.out",
            opacity: 1,
            scrollTrigger: {
              trigger: wrapper,
              start: disableLottie ? "top bottom" : "top+=1px top",
              end: "bottom",
              scrub: true
            }
          });
          gsap.to(videoContainer, {
            scrollTrigger: {
              toggleActions: "play pause pause reverse",
              trigger: wrapper,
              start: pinStart,
              endTrigger: chubiEnd,
              end: "bottom center-=25%",
              pin: videoContainer,
              pinSpacing: false,
              anticipatePin: 1
            }
          });
          gsap.from(split.lines, {
            duration: 0.5,
            yPercent: 100,
            stagger: 0.15,
            ease: "power2.out",
            scrollTrigger: {
              trigger: titleText,
              toggleActions: "play none none reverse",
              start: "center bottom",
              end: "center top",
              fastScrollEnd: false
            }
          });
        });
      }, wrapper);
      return () => {
        ctx.revert();
        split.revert();
      };
    };
    $[9] = disableLottie;
    $[10] = lottieRef;
    $[11] = playAnimHandler;
    $[12] = playBackAnimHandler;
    $[13] = playForwardAnimHandler;
    $[14] = scrollTriggerBackStart;
    $[15] = setSpeedHandler;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== disableLottie || $[18] !== scrollTriggerBackStart) {
    t10 = [scrollTriggerBackStart, disableLottie];
    $[17] = disableLottie;
    $[18] = scrollTriggerBackStart;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  useLayoutEffect(t9, t10);
  const t11 = disableLottie && css.disableLottie;
  let t12;
  if ($[20] !== t11) {
    t12 = clsx("wrapper", css.wrapper, t11);
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== aboutUsVideo || $[23] !== device) {
    t13 = device === "mobile" && typeof aboutUsVideo !== "undefined" && <div className={css.heroVideoWrapper}><VideoPreview videoUrl={aboutUsVideo.videoData.videoLink} thumbnailUrl={aboutUsVideo.videoData.videoThumbnailUrl} onClick={aboutUsVideo.openAboutUsVideo} /></div>;
    $[22] = aboutUsVideo;
    $[23] = device;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== brushData || $[26] !== device || $[27] !== disableLottie || $[28] !== lottieRef) {
    t14 = device === "desktop" && (disableLottie ? <div aria-hidden={true} className={clsx(css.lottieContainer, css.lottieContainerSpacer)} /> : <div className={css.lottieContainer}>{brushData ? <Lottie id="lottie" lottieRef={lottieRef} className={css.lottieWrapper} animationData={brushData} loop={false} autoplay={false} rendererSettings={{
        preserveAspectRatio: "none",
        progressiveLoad: true
      }} /> : null}</div>);
    $[25] = brushData;
    $[26] = device;
    $[27] = disableLottie;
    $[28] = lottieRef;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== disableLottie) {
    t15 = !disableLottie && <div className={css.empty} />;
    $[30] = disableLottie;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== setCursorVariant) {
    t16 = () => setCursorVariant("lottie-purple-explore-more");
    $[32] = setCursorVariant;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      position: "relative"
    };
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const t18 = disableLottie && css.videoWrapperLobby;
  let t19;
  if ($[35] !== t18) {
    t19 = clsx(css.videoWrapper, t18);
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <video autoPlay={true} loop={true} playsInline={true} muted={true} controls={false}><source src="/assets/video/mov/service-chub.mov" type={"video/quicktime; codecs=\"hvc1\""} /><source src="/assets/video/webm/service-chub.webm" type="video/webm" /></video>;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t19) {
    t21 = <div className={t19}>{t20}</div>;
    $[38] = t19;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  const t22 = disableLottie ? "h2" : "h1";
  let t23;
  if ($[40] !== title) {
    t23 = <StrapiRichContent content={title} />;
    $[40] = title;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== headingTag || $[43] !== t22 || $[44] !== t23) {
    t24 = <Typography uppercase={true} className={css.title} variant={t22} tag={headingTag}>{t23}</Typography>;
    $[42] = headingTag;
    $[43] = t22;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t21 || $[47] !== t24) {
    t25 = <div className="container" style={t17}>{t21}{t24}</div>;
    $[46] = t21;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Image className={css.graffitiBg} width={500} height={500} src="/assets/images/graffiti-service.svg" alt="Graffiti" />;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  const t27 = headingTag === "h1" ? "h2" : "h3";
  let t28;
  if ($[50] !== serviceList || $[51] !== t27) {
    t28 = <ServiceList ref={chubiEndRef} serviceList={serviceList} itemHeadingTag={t27} />;
    $[50] = serviceList;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== t16 || $[54] !== t25 || $[55] !== t28) {
    t29 = <div className={css.contentWrapper} onMouseEnter={t16}>{t25}{t26}{t28}</div>;
    $[53] = t16;
    $[54] = t25;
    $[55] = t28;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className={css.curvedContainer}><div><Image width={1775} height={1775} src="/assets/images/circle-mask.svg" alt="lets add some qream on top" className={css.curvedMask} /></div></div>;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  const t31 = device === "desktop" ? 1775 : 424;
  const t32 = device === "desktop" ? 1775 : 424;
  const t33 = device === "desktop" ? "/assets/images/circle-text-desk.webp" : "/assets/images/circle-text-mobile.webp";
  let t34;
  if ($[58] !== t31 || $[59] !== t32 || $[60] !== t33) {
    t34 = <div className={css.curvedContainer}><Image width={t31} height={t32} alt="lets add some qream on top" className={css.curvedText} src={t33} /></div>;
    $[58] = t31;
    $[59] = t32;
    $[60] = t33;
    $[61] = t34;
  } else {
    t34 = $[61];
  }
  let t35;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className={css.bottomClearFix} />;
    $[62] = t35;
  } else {
    t35 = $[62];
  }
  let t36;
  if ($[63] !== t34) {
    t36 = <div className={css.curvedWrapper}>{t30}{t34}{t35}</div>;
    $[63] = t34;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] !== htmlId || $[66] !== t12 || $[67] !== t13 || $[68] !== t14 || $[69] !== t15 || $[70] !== t29 || $[71] !== t36) {
    t37 = <section ref={wrapperRef} className={t12} id={htmlId}>{t13}{t14}{t15}{t29}{t36}</section>;
    $[65] = htmlId;
    $[66] = t12;
    $[67] = t13;
    $[68] = t14;
    $[69] = t15;
    $[70] = t29;
    $[71] = t36;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  return t37;
};
export default Services;